// src/ui/ViewDeckModal.ts
//
// Phase 13 (WHEEL-11, UI-18): read-only modal listing the current numbers deck
// composition + void positions. Reuses ConfirmModal's depth-stacking +
// clickjack-guard contract (see src/ui/ConfirmModal.ts); no Yes/No buttons
// (Close only). Singleton-per-scene; double-tap idempotent.
//
// Depth contract (consistent with ConfirmModal):
//   200 — overlay (blocks underlying taps; clickjack guard)
//   201 — panel background
//   202 — content text, close button rect
//   203 — close button label
//
// Per 13-UI-SPEC.md §"View-Deck Modal":
//   Panel: 320×400px centered at (187, 333), fill #0a3d20, stroke #ffd700 2px
//   Title: "Numbers Deck" 14px white at (187, 145)
//   Subtitle: "{N} numbers · {V} void slots" 11px #cccccc at (187, 162)
//   Numbers list: sorted ascending, 6 per row, 11px, color-coded
//   Void heading: "Void Positions" 11px #888888 at (187, 350)
//   Void list: comma-sep positions 11px #888888 at (187, 365)
//   Close button: 200×44px pill, fill #064020, stroke #ffffff 1px at (187, 415)
//   Close label: "Close" 11px #ffffff at (187, 415), depth 203

import Phaser from 'phaser';
import type { SlotNumber } from '../state/types';
import { RED_NUMBERS } from '../systems/wheel';
import { addCrispText } from './textHelper';

export class ViewDeckModal {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  // 12-REVIEW ME-01 pattern: in-flight guard prevents double-fire on close
  // during the 1-2 frame window between close() destroying the container and
  // the pointer-event queue draining.
  private resolved = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  open(numbersDeck: readonly SlotNumber[], voidPositions: readonly number[]): void {
    if (this.container) return; // singleton-per-scene; ignore double-open
    this.resolved = false;

    const objs: Phaser.GameObjects.GameObject[] = [];

    // Depth 200: full-screen dimmed overlay.
    // ME-04 pattern: explicit no-op pointerdown absorbs taps so underlying
    // scene objects (spin button, board cells) are never reached through the
    // overlay. setInteractive() alone is fragile mid-scene; explicit handler
    // makes the clickjack guard unconditional.
    const overlay = this.scene.add
      .rectangle(0, 0, 375, 667, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setDepth(200)
      .setInteractive();
    overlay.on('pointerdown', () => {
      // intentional no-op — overlay tap does NOT close (clickjack guard)
    });
    objs.push(overlay);

    // Depth 201: centered panel, gold border.
    const panel = this.scene.add
      .rectangle(187, 333, 320, 400, 0x0a3d20)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0xffd700)
      .setDepth(201);
    objs.push(panel);

    // Depth 202: title.
    objs.push(
      addCrispText(this.scene, 187, 145, 'Numbers Deck', {
        color: '#ffffff',
        fontSize: '14px',
      })
        .setOrigin(0.5)
        .setDepth(202),
    );

    // Depth 202: subtitle — "{N} numbers · {V} void slots" or "No void slots" variant.
    const subtitleText =
      voidPositions.length === 0
        ? `${numbersDeck.length} numbers · No void slots`
        : `${numbersDeck.length} numbers · ${voidPositions.length} void slots`;
    objs.push(
      addCrispText(this.scene, 187, 162, subtitleText, {
        color: '#cccccc',
        fontSize: '11px',
      })
        .setOrigin(0.5)
        .setDepth(202),
    );

    // Depth 202: numbers list — sorted ascending, 6 per row, color-coded.
    //   0 → #5dd07a (green zero)
    //   Red numbers (per RED_NUMBERS set) → #ff5060
    //   Black numbers → #cccccc
    // Grid: 6 cols × 40px = 240px; centered around x=187 → startX = 187 - 120 + 20 = 87.
    const sorted = [...numbersDeck].sort((a, b) => a - b);
    const cols = 6;
    const cellW = 40; // 6 × 40 = 240px centered in 320px panel
    const startX = 187 - (cols * cellW) / 2 + cellW / 2; // = 87
    const startY = 188;
    const rowH = 14;
    sorted.forEach((n, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const color =
        n === 0
          ? '#5dd07a'
          : RED_NUMBERS.has(n)
            ? '#ff5060'
            : '#cccccc';
      objs.push(
        addCrispText(this.scene, startX + col * cellW, startY + row * rowH, String(n), {
          color,
          fontSize: '11px',
        })
          .setOrigin(0.5)
          .setDepth(202),
      );
    });

    // Depth 202: void section heading.
    objs.push(
      addCrispText(this.scene, 187, 350, 'Void Positions', {
        color: '#888888',
        fontSize: '11px',
      })
        .setOrigin(0.5)
        .setDepth(202),
    );

    // Depth 202: void list — comma-separated sorted positions or "No void slots".
    const voidLine =
      voidPositions.length === 0
        ? 'No void slots'
        : [...voidPositions].sort((a, b) => a - b).join(', ');
    objs.push(
      addCrispText(this.scene, 187, 365, voidLine, {
        color: '#888888',
        fontSize: '11px',
      })
        .setOrigin(0.5)
        .setDepth(202),
    );

    // Depth 202: Close button (200×44px pill).
    const closeBtn = this.scene.add
      .rectangle(187, 415, 200, 44, 0x064020)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(202)
      .setInteractive({ useHandCursor: true });
    objs.push(closeBtn);

    // Depth 203: Close label.
    const closeLbl = addCrispText(this.scene, 187, 415, 'Close', {
      color: '#ffffff',
      fontSize: '11px',
    })
      .setOrigin(0.5)
      .setDepth(203);
    objs.push(closeLbl);

    // Container groups all elements; destroyed together on close().
    this.container = this.scene.add.container(0, 0, objs).setDepth(200);

    // ME-01 pattern: idempotency guard prevents double-fire during the
    // close()→next-frame race window. disableInteractive() provides
    // defense-in-depth so a queued pointer event can't slip past the flag.
    closeBtn.on('pointerdown', () => {
      if (this.resolved) return;
      this.resolved = true;
      closeBtn.disableInteractive();
      this.close();
    });
  }

  close(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  isOpen(): boolean {
    return this.container !== null;
  }
}
